import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Badge as BadgeIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CloudUpload as PushIcon,
  CloudSync as SyncIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as OnDeviceIcon,
  RadioButtonUnchecked as OffDeviceIcon,
  PersonAdd as PersonAddIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { attendanceAPI, DeviceUserRow, StandaloneUserRow } from '../../../services/api';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { extractErrorMessage } from '../../../utils/errorHandler';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const HR_COLOR = '#009688';

// ADMS Verify modes (Appendix 7). Value pushed to the clock as USERINFO Verify.
const VERIFY_MODES: { value: number; label: string }[] = [
  { value: 18, label: 'Visage + carte' },
  { value: 15, label: 'Visage seul' },
  { value: 4, label: 'Carte seule' },
  { value: 0, label: 'Auto (visage ou carte)' },
  { value: -1, label: 'Défaut appareil' },
];

// Column-header filters (applied client-side — see note in the component).
const FILTER_SCHEMA = {
  matricule:   { type: 'text',   param: 'matricule' },
  nom:         { type: 'text',   param: 'nom' },
  pin:         { type: 'text',   param: 'pin' },
  verify_mode: { type: 'select', param: 'verify_mode' },
  on_device:   { type: 'select', param: 'on_device' },
} as const;

const UtilisateursPointeuse: React.FC = () => {
  const [rows, setRows] = useState<DeviceUserRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [standalone, setStandalone] = useState<StandaloneUserRow[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editingPin, setEditingPin] = useState<string | null>(null); // null = adding
  const [newPin, setNewPin] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newVerifyMode, setNewVerifyMode] = useState<number>(18);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Column-header filters — applied client-side. This table holds at most a few
  // hundred active employees, so we load them all at once and filter/paginate in
  // the browser (same ColumnFilter UI as Pointage Biométrique, no backend change).
  const { filters, update, reset, hasActive } = useColumnFilters(FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      // Load the whole active-employee list in one shot (endpoint caps at 500).
      const res = await attendanceAPI.getDeviceUsers({ limit: 500 });
      setRows(res.items);
    } catch (err: any) {
      showError(extractErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Global search box + per-column filters, all client-side over the loaded rows.
  const filteredRows = useMemo(() => {
    const g = searchTerm.trim().toLowerCase();
    const fMat = (filters.matricule as string).trim().toLowerCase();
    const fNom = (filters.nom as string).trim().toLowerCase();
    const fPin = (filters.pin as string).trim().toLowerCase();
    const fMode = filters.verify_mode as string;   // '' = all
    const fOnDev = filters.on_device as string;     // '' = all, else 'true' / 'false'
    return rows.filter((r) => {
      if (g) {
        const hay = `${r.matricule || ''} ${r.nom_complet || ''} ${r.device_user_id || ''}`.toLowerCase();
        if (!hay.includes(g)) return false;
      }
      if (fMat && !(r.matricule || '').toLowerCase().includes(fMat)) return false;
      if (fNom && !(r.nom_complet || '').toLowerCase().includes(fNom)) return false;
      if (fPin && !String(r.device_user_id ?? '').toLowerCase().includes(fPin)) return false;
      if (fMode !== '' && String(r.device_verify_mode) !== fMode) return false;
      if (fOnDev !== '' && String(r.on_device) !== fOnDev) return false;
      return true;
    });
  }, [rows, searchTerm, filters]);

  const total = filteredRows.length;
  const pageRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const fetchStandalone = useCallback(async () => {
    try {
      const res = await attendanceAPI.getStandaloneUsers();
      setStandalone(res.items);
    } catch {
      /* non-critical */
    }
  }, []);

  useEffect(() => { fetchStandalone(); }, [fetchStandalone]);

  // Device commands apply asynchronously (the clock acks on its next ~10s
  // heartbeat, and only then does our mirror update). Refetch a few times so the
  // table reflects the change once the device confirms it, without polling.
  const refreshSoon = useCallback(() => {
    fetchRows();
    fetchStandalone();
    [4000, 9000, 14000].forEach((ms) => setTimeout(() => { fetchRows(); fetchStandalone(); }, ms));
  }, [fetchRows, fetchStandalone]);

  const openAdd = () => {
    setEditingPin(null);
    setNewPin('');
    setNewName('');
    setNewPassword('');
    setNewVerifyMode(18);
    setAddOpen(true);
  };

  const openEdit = (u: StandaloneUserRow) => {
    setEditingPin(u.pin);
    setNewPin(u.pin);
    setNewName(u.name || '');
    setNewPassword(''); // device never reports passwords back; blank = keep current
    setNewVerifyMode(u.verify_mode ?? 18);
    setAddOpen(true);
  };

  const handleSubmitManual = async () => {
    const pin = newPin.trim();
    const name = newName.trim();
    const password = newPassword.trim();
    if (!pin || !name) {
      showError('Le PIN et le nom sont obligatoires.');
      return;
    }
    if (password && !/^\d{1,8}$/.test(password)) {
      showError('Le mot de passe doit être numérique (max 8 chiffres).');
      return;
    }
    try {
      setBusy(true);
      if (editingPin !== null) {
        await attendanceAPI.editManualUser(editingPin, pin, name, password, newVerifyMode);
        showSuccess(`Modification envoyée à l'appareil (PIN ${pin}). Elle s'appliquera une fois confirmée (~10s).`);
      } else {
        await attendanceAPI.addManualUser(pin, name, password, newVerifyMode);
        showSuccess(`${name} (PIN ${pin}) envoyé à l'appareil. Il apparaîtra une fois confirmé (~10s).`);
      }
      setAddOpen(false);
      setEditingPin(null);
      setNewPin('');
      setNewName('');
      setNewPassword('');
      refreshSoon();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleStandaloneVerify = async (u: StandaloneUserRow, mode: number) => {
    if (mode === u.verify_mode) return;
    const label = VERIFY_MODES.find((m) => m.value === mode)?.label ?? mode;
    try {
      setBusy(true);
      // Re-push with the same PIN + name, new method (blank password = keep current).
      await attendanceAPI.editManualUser(u.pin, u.pin, u.name || '', undefined, mode);
      setStandalone((prev) => prev.map((s) => (s.pin === u.pin ? { ...s, verify_mode: mode } : s)));
      showSuccess(`Méthode « ${label} » envoyée à l'appareil (PIN ${u.pin}, ~10s).`);
    } catch (err: any) {
      showError(extractErrorMessage(err));
      fetchStandalone();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteStandalone = (u: StandaloneUserRow) => {
    showConfirm(
      `Supprimer l'utilisateur PIN ${u.pin}${u.name ? ` (${u.name})` : ''} de l'appareil ?`,
      async () => {
        try {
          setBusy(true);
          await attendanceAPI.deleteDeviceUser(u.pin);
          showSuccess(`Suppression envoyée à l'appareil (PIN ${u.pin}). Effective une fois confirmée (~10s).`);
          refreshSoon();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setBusy(false);
        }
      },
      { title: "Supprimer de l'appareil", confirmText: 'Supprimer', cancelText: 'Annuler' }
    );
  };

  const handlePushAll = () => {
    showConfirm(
      "Envoyer tous les employés (avec un PIN) vers l'appareil ? L'appareil créera/mettra à jour ces utilisateurs à son prochain contact (~10s).",
      async () => {
        try {
          setBusy(true);
          const res = await attendanceAPI.pushUsers();
          showSuccess(`${res.employees} employé(s) envoyé(s) à l'appareil. Statut mis à jour une fois confirmé (~10s).`);
          refreshSoon();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setBusy(false);
        }
      },
      { title: "Envoyer vers l'appareil", confirmText: 'Envoyer', cancelText: 'Annuler' }
    );
  };

  const handlePushOne = async (row: DeviceUserRow) => {
    if (!row.device_user_id) {
      showError("Attribuez d'abord un PIN à cet employé.");
      return;
    }
    try {
      setBusy(true);
      await attendanceAPI.pushUsers(row.id);
      showSuccess(`${row.nom_complet} envoyé à l'appareil (PIN ${row.device_user_id}). Statut mis à jour une fois confirmé (~10s).`);
      refreshSoon();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleQuery = async () => {
    try {
      setBusy(true);
      await attendanceAPI.queryUsers();
      showSuccess(`Synchronisation demandée à l'appareil. Liste mise à jour dans quelques secondes.`);
      refreshSoon();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyMode = async (row: DeviceUserRow, mode: number) => {
    if (mode === row.device_verify_mode) return;
    if (!row.device_user_id) {
      showError("Attribuez d'abord un PIN à cet employé.");
      return;
    }
    const label = VERIFY_MODES.find((m) => m.value === mode)?.label ?? mode;
    try {
      setBusy(true);
      await attendanceAPI.setVerifyMode(row.id, mode);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, device_verify_mode: mode } : r)));
      showSuccess(`Méthode « ${label} » envoyée à l'appareil pour ${row.nom_complet} (~10s).`);
    } catch (err: any) {
      showError(extractErrorMessage(err));
      fetchRows();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = (row: DeviceUserRow) => {
    if (!row.device_user_id) return;
    showConfirm(
      `Supprimer l'utilisateur PIN ${row.device_user_id} (${row.nom_complet}) de l'appareil ?`,
      async () => {
        try {
          setBusy(true);
          await attendanceAPI.deleteDeviceUser(row.device_user_id as string);
          showSuccess(`Suppression envoyée à l'appareil (PIN ${row.device_user_id}). Effective une fois confirmée (~10s).`);
          refreshSoon();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setBusy(false);
        }
      },
      { title: "Supprimer de l'appareil", confirmText: 'Supprimer', cancelText: 'Annuler' }
    );
  };

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <BadgeIcon sx={{ color: HR_COLOR }} />
          <Typography variant="h5">Configuration Pointeuse</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} disabled={busy}
            onClick={openAdd} sx={{ color: HR_COLOR, borderColor: HR_COLOR }}>
            Ajouter utilisateur
          </Button>
          <Button variant="outlined" size="small" startIcon={<SyncIcon />} disabled={busy}
            onClick={handleQuery} sx={{ color: HR_COLOR, borderColor: HR_COLOR }}>
            Synchroniser
          </Button>
          <Button variant="contained" size="small" startIcon={busy ? <CircularProgress size={16} /> : <PushIcon />}
            disabled={busy} onClick={handlePushAll}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00796b' } }}>
            Envoyer vers l'appareil
          </Button>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={fetchRows} sx={{ color: HR_COLOR }}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Attribuez un PIN à chaque employé puis envoyez-les vers la pointeuse (nom + PIN).
        L'empreinte / le visage restent à enrôler physiquement sur l'appareil une seule fois.
      </Typography>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small" placeholder="Rechercher par nom, matricule ou PIN…"
          value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />
        {(hasActive() || searchTerm) && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={() => { setSearchTerm(''); reset(); setPage(0); }}
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            Effacer les filtres
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={getModuleTableTheme('hr')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={modernCellStyles}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Matricule
                  <ColumnFilter type="text" value={filters.matricule}
                    onChange={(v) => update('matricule', v)} label="matricule"
                    placeholder="Matricule…" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={modernCellStyles}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Employé
                  <ColumnFilter type="text" value={filters.nom}
                    onChange={(v) => update('nom', v)} label="employé"
                    placeholder="Nom…" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  PIN Pointeuse
                  <ColumnFilter type="text" value={filters.pin}
                    onChange={(v) => update('pin', v)} label="PIN"
                    placeholder="PIN…" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Méthode
                  <ColumnFilter type="select" value={filters.verify_mode}
                    onChange={(v) => update('verify_mode', v)} label="méthode"
                    options={VERIFY_MODES.map((m) => ({ value: String(m.value), label: m.label }))}
                    color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Sur l'appareil
                  <ColumnFilter type="select" value={filters.on_device}
                    onChange={(v) => update('on_device', v)} label="sur l'appareil"
                    options={[{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }]}
                    color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <CircularProgress size={28} sx={{ color: HR_COLOR }} />
              </TableCell></TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Aucun employé trouvé
              </TableCell></TableRow>
            ) : (
              pageRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.matricule}</TableCell>
                  <TableCell>{r.nom_complet}</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>
                    {r.device_user_id ?? '—'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Select
                      size="small"
                      value={r.device_verify_mode}
                      disabled={busy || !r.device_user_id}
                      onChange={(e) => handleVerifyMode(r, Number(e.target.value))}
                      sx={{ minWidth: 150, fontSize: '0.8rem' }}
                    >
                      {VERIFY_MODES.map((m) => (
                        <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.8rem' }}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {r.on_device ? (
                      <Chip size="small" color="success" variant="outlined" icon={<OnDeviceIcon />} label="Oui" />
                    ) : (
                      <Chip size="small" variant="outlined" icon={<OffDeviceIcon />} label="Non" />
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip title="Envoyer cet employé vers l'appareil">
                      <span>
                        <IconButton size="small" disabled={busy || !r.device_user_id}
                          onClick={() => handlePushOne(r)} sx={{ color: HR_COLOR }}>
                          <PushIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Supprimer de l'appareil">
                      <span>
                        <IconButton size="small" disabled={busy || !r.device_user_id}
                          onClick={() => handleDelete(r)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Lignes par page"
        />
      </TableContainer>

      {standalone.length > 0 && (
        <Box mt={4}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: HR_COLOR }}>
            Utilisateurs hors personnel
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            PIN ajoutés manuellement, non liés à un employé. Leurs pointages s'affichent comme « Utilisateur {'<PIN>'} ».
          </Typography>
          <TableContainer component={Paper} sx={getModuleTableTheme('hr')}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>PIN</TableCell>
                  <TableCell sx={modernCellStyles}>Nom</TableCell>
                  <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>Méthode</TableCell>
                  <TableCell sx={{ ...modernCellStyles, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standalone.map((u) => (
                  <TableRow key={u.pin} hover>
                    <TableCell sx={{ textAlign: 'center' }}>{u.pin}</TableCell>
                    <TableCell>{u.name || '—'}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Select
                        size="small"
                        value={u.verify_mode}
                        disabled={busy}
                        onChange={(e) => handleStandaloneVerify(u, Number(e.target.value))}
                        sx={{ minWidth: 150, fontSize: '0.8rem' }}
                      >
                        {VERIFY_MODES.map((m) => (
                          <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.8rem' }}>{m.label}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title="Modifier (nom / PIN)">
                        <span>
                          <IconButton size="small" disabled={busy}
                            onClick={() => openEdit(u)} sx={{ color: HR_COLOR }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Supprimer de l'appareil">
                        <span>
                          <IconButton size="small" disabled={busy}
                            onClick={() => handleDeleteStandalone(u)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingPin !== null ? 'Modifier un utilisateur (hors personnel)' : 'Ajouter un utilisateur (hors personnel)'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingPin !== null
              ? "Modifie le nom et/ou le PIN. Changer le PIN supprime l'ancien utilisateur sur l'appareil et en crée un nouveau (la personne devra ré-enrôler son empreinte / visage)."
              : "Envoie directement un PIN + nom vers la pointeuse, sans lien avec un employé. La personne devra enrôler son empreinte / visage une fois sur l'appareil."}
          </Typography>
          <TextField
            autoFocus fullWidth size="small" label="PIN" value={newPin}
            onChange={(e) => setNewPin(e.target.value)} sx={{ mb: 2 }}
            placeholder="ex. 500"
          />
          <TextField
            fullWidth size="small" label="Nom" value={newName}
            onChange={(e) => setNewName(e.target.value)} sx={{ mb: 2 }}
            placeholder="ex. Agent Sécurité"
          />
          <TextField
            fullWidth size="small" label="Mot de passe (optionnel)" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} sx={{ mb: 2 }}
            placeholder="ex. 1234"
            inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            helperText={
              editingPin !== null
                ? 'Code numérique tapé sur la pointeuse (max 8 chiffres). Laisser vide pour conserver l’actuel.'
                : 'Code numérique tapé sur la pointeuse (max 8 chiffres). Optionnel.'
            }
          />
          <Select
            fullWidth size="small" value={newVerifyMode}
            onChange={(e) => setNewVerifyMode(Number(e.target.value))}
          >
            {VERIFY_MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>Méthode : {m.label}</MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={busy}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmitManual} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : <PushIcon />}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00796b' } }}>
            {editingPin !== null ? 'Enregistrer' : "Envoyer vers l'appareil"}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default UtilisateursPointeuse;
